/**
 * Tab "Correos del banco" dentro de Pagos (Tesorería).
 *
 * Muestra los correos de ingesta de pagos: sobre todo los DESCARTADOS (formato
 * no reconocido, error de parser, duplicados) para que el tesorero los vea y
 * pueda RECUPERARLOS con "Reprocesar" cuando se incorpora un template nuevo
 * (ej. un formato de banco antes no soportado). Reprocesar re-corre el parser
 * sobre el correo guardado y, si ahora se reconoce, crea el pago.
 *
 * Solo lectura + acción de reproceso; la clasificación del pago resultante se
 * hace en el tab "Por distribuir" como cualquier pago.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  getCuerpoEmail,
  listarEmailsRecibidos,
  marcarRevisadoEmail,
  reprocesarEmail,
  type AccionEmailRecibido,
  type EmailCuerpo,
  type EmailRecibidoRead,
} from '../../lib/api/emails-recibidos';
import { formatearFechaHora } from '../../lib/tiempo';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

type Filtro = 'por_reprocesar' | 'todos';

// Reprocesable = descarte por "formato no reconocido" (template_no_detectado)
// o por "no se pudo leer" (error_parser*, el banco estaba detectado pero el
// parser falló y luego se arregló). Los demás (saliente, cartola, duplicado)
// NO se recuperan: mostrar "Reprocesar" ahí sería un falso positivo (el
// backend igual lo rechaza).
function esReprocesable(em: EmailRecibidoRead): boolean {
  const motivo = em.motivo_descarte ?? '';
  return (
    em.accion === 'descartado_irrelevante' &&
    (motivo === 'template_no_detectado' || motivo.startsWith('error_parser'))
  );
}

function detalleError(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.body === 'object' &&
      err.body !== null &&
      'detail' in err.body
      ? String((err.body as { detail: unknown }).detail)
      : `Error ${err.status}`;
  }
  return 'Error de red';
}

/** Traduce el motivo_descarte técnico a algo legible para el tesorero. */
function motivoLegible(motivo: string | null): string {
  if (!motivo) return '';
  if (motivo === 'template_no_detectado')
    return 'Formato no reconocido (banco/plantilla nueva)';
  if (motivo === 'email_saliente') return 'Transferencia saliente (no un depósito)';
  if (motivo === 'cartola_mensual') return 'Cartola mensual (informativo)';
  if (motivo.startsWith('duplicado_de_pago')) return 'Duplicado de un pago ya registrado';
  if (motivo.startsWith('duplicado_por_message_id')) return 'Correo duplicado (reenviado)';
  if (motivo.startsWith('error_parser')) return `No se pudo leer: ${motivo}`;
  return motivo;
}

function BadgeAccion({ accion }: { accion: AccionEmailRecibido }) {
  return accion === 'pago_creado' ? (
    <Badge variant="success">Pago creado</Badge>
  ) : (
    <Badge variant="warning">Descartado</Badge>
  );
}

export function CorreosBancoTab() {
  const [emails, setEmails] = useState<EmailRecibidoRead[]>([]);
  const [filtro, setFiltro] = useState<Filtro>('por_reprocesar');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reprocesandoId, setReprocesandoId] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // "Ver correo": modal con el cuerpo decodificado. `verId` marca cuál se
  // está cargando/mostrando; `cuerpo` el contenido ya decodificado.
  const [verId, setVerId] = useState<string | null>(null);
  const [cuerpo, setCuerpo] = useState<EmailCuerpo | null>(null);
  const [cuerpoError, setCuerpoError] = useState<string | null>(null);
  const [marcandoId, setMarcandoId] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (filtro === 'todos') {
        setEmails(await listarEmailsRecibidos({ limit: 50 }));
      } else {
        // "Por reprocesar": descartes recuperables (formato no reconocido /
        // error de parser) que AÚN no fueron revisados. Excluye
        // salientes/cartolas/duplicados y los ya marcados como descarte
        // definitivo (revisado_at).
        const descartados = await listarEmailsRecibidos({
          accion: 'descartado_irrelevante',
          limit: 50,
        });
        setEmails(
          descartados.filter(
            (em) => esReprocesable(em) && em.revisado_at === null,
          ),
        );
      }
    } catch {
      setError('No pudimos cargar los correos. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), 5000);
    return () => clearTimeout(t);
  }, [aviso]);

  async function onReprocesar(em: EmailRecibidoRead) {
    if (reprocesandoId) return;
    setReprocesandoId(em.id);
    setError(null);
    try {
      await reprocesarEmail(em.id);
      setAviso(
        'Correo reprocesado: se creó el pago. Búscalo en "Por distribuir".',
      );
      await cargar();
    } catch (err) {
      setError(`No se pudo reprocesar: ${detalleError(err)}`);
    } finally {
      setReprocesandoId(null);
    }
  }

  async function onVer(em: EmailRecibidoRead) {
    setVerId(em.id);
    setCuerpo(null);
    setCuerpoError(null);
    try {
      setCuerpo(await getCuerpoEmail(em.id));
    } catch (err) {
      setCuerpoError(`No se pudo leer el correo: ${detalleError(err)}`);
    }
  }

  function cerrarVer() {
    setVerId(null);
    setCuerpo(null);
    setCuerpoError(null);
  }

  async function onMarcarRevisado(em: EmailRecibidoRead) {
    if (marcandoId) return;
    // Salvaguarda: si el correo es reprocesable (posible depósito real aún no
    // procesado), pedir confirmación antes de sacarlo de los pendientes.
    if (
      esReprocesable(em) &&
      !window.confirm(
        'Este correo podría ser un depósito real recuperable con "Reprocesar". ' +
          'Marcarlo como descarte definitivo lo sacará de los pendientes. ' +
          '¿Continuar de todos modos?',
      )
    ) {
      return;
    }
    setMarcandoId(em.id);
    setError(null);
    try {
      await marcarRevisadoEmail(em.id);
      setAviso('Correo marcado como revisado; ya no aparece como pendiente.');
      await cargar();
    } catch (err) {
      setError(`No se pudo marcar: ${detalleError(err)}`);
    } finally {
      setMarcandoId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-text-secondary">Mostrar:</span>
        {(
          [
            ['por_reprocesar', 'Por reprocesar'],
            ['todos', 'Todos'],
          ] as [Filtro, string][]
        ).map(([val, label]) => (
          <button
            key={val}
            type="button"
            onClick={() => setFiltro(val)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              filtro === val
                ? 'border-orange text-orange bg-orange/10'
                : 'border-white/15 text-text-muted hover:text-text-secondary'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <p className="text-xs text-text-secondary">
        Correos de bancos que llegaron a la casilla. Si un depósito no aparece
        en "Por distribuir", suele estar acá descartado por un formato no
        reconocido; usa "Reprocesar" para recuperarlo.
      </p>

      {aviso && (
        <div className="bg-soft-success/40 border-l-4 border-state-success p-3 rounded text-sm text-text-on-success">
          {aviso}
        </div>
      )}
      {error && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded text-sm text-text-primary">
          {error}
        </div>
      )}
      {loading && <p className="text-sm text-text-muted">Cargando…</p>}

      {!loading && !error && emails.length === 0 && (
        <div className="bg-bg-elevated rounded-lg shadow p-5 text-sm text-text-secondary">
          No hay correos para este filtro.
        </div>
      )}

      {!loading &&
        emails.map((em) => (
          <article
            key={em.id}
            className="bg-bg-elevated rounded-lg shadow p-4 flex flex-wrap items-start justify-between gap-3"
          >
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <BadgeAccion accion={em.accion} />
                <span className="text-sm font-medium text-text-primary truncate">
                  {em.asunto}
                </span>
              </div>
              <div className="text-xs text-text-secondary truncate">
                {em.remitente} · {formatearFechaHora(em.recibido_at)}
              </div>
              {em.motivo_descarte && (
                <div className="text-xs text-state-warning">
                  Motivo: {motivoLegible(em.motivo_descarte)}
                </div>
              )}
              {em.revisado_at && (
                <div className="text-xs text-text-muted">
                  ✓ Revisado (descarte definitivo)
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="secondary" size="sm" onClick={() => onVer(em)}>
                Ver correo
              </Button>
              {esReprocesable(em) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onReprocesar(em)}
                  disabled={reprocesandoId !== null}
                >
                  {reprocesandoId === em.id ? 'Reprocesando…' : 'Reprocesar'}
                </Button>
              )}
              {em.accion === 'descartado_irrelevante' && !em.revisado_at && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMarcarRevisado(em)}
                  disabled={marcandoId !== null}
                >
                  {marcandoId === em.id ? 'Marcando…' : 'Descarte definitivo'}
                </Button>
              )}
            </div>
          </article>
        ))}

      {verId && (
        <Modal title="Correo recibido" onClose={cerrarVer}>
          {cuerpoError ? (
            <p className="text-sm text-state-danger">{cuerpoError}</p>
          ) : !cuerpo ? (
            <p className="text-sm text-text-muted">Cargando…</p>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-text-secondary">
                <div>
                  <span className="text-text-muted">De:</span>{' '}
                  {cuerpo.remitente}
                </div>
                <div>
                  <span className="text-text-muted">Asunto:</span>{' '}
                  {cuerpo.asunto}
                </div>
              </div>
              <pre className="text-sm text-text-primary whitespace-pre-wrap break-words bg-bg-sunken rounded p-3 max-h-[50vh] overflow-y-auto">
                {cuerpo.cuerpo_texto || '(Sin cuerpo de texto — puede venir solo con adjunto.)'}
              </pre>
              <p className="text-xs text-text-muted">
                Los archivos adjuntos (ej. comprobantes PDF) no se muestran acá.
              </p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
